import {
    forwardRef,
    type ReactNode,
} from 'react'

import {
    Input as AriaInput,
    TextField as AriaTextField,
    type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components'

import {
    cva,
    type VariantProps,
} from 'class-variance-authority'

import {
    FormField,
    type FormFieldErrorMessage,
} from './form-field'

import { cn } from '@/shared/lib/cn'

const textFieldControlVariants = cva(
    [
        'flex w-full items-center gap-2',
        'rounded-lg border',
        'bg-[var(--color-surface)]',
        'transition-[border-color,box-shadow,background-color]',
        'focus-within:ring-2',
        'focus-within:ring-offset-1',
    ],
    {
        variants: {
            size: {
                small: 'min-h-9 px-3',
                medium: 'min-h-10 px-3',
                large: 'min-h-12 px-4',
            },

            isInvalid: {
                true: [
                    'border-[var(--color-danger)]',
                    'focus-within:border-[var(--color-danger)]',
                    'focus-within:ring-[var(--color-danger)]/20',
                ],

                false: [
                    'border-[var(--color-border)]',
                    'focus-within:border-[var(--color-primary)]',
                    'focus-within:ring-[var(--color-primary)]/20',
                ],
            },

            isDisabled: {
                true: [
                    'cursor-not-allowed',
                    'bg-[var(--color-surface-muted)]',
                    'opacity-60',
                ],

                false: '',
            },

            isReadOnly: {
                true: [
                    'bg-[var(--color-surface-muted)]',
                ],

                false: '',
            },
        },

        defaultVariants: {
            size: 'medium',
            isInvalid: false,
            isDisabled: false,
            isReadOnly: false,
        },
    },
)

const textFieldInputVariants = cva(
    [
        'min-w-0 flex-1',
        'border-0 bg-transparent',
        'text-[var(--color-foreground)]',
        'outline-none',
        'placeholder:text-[var(--color-muted-foreground)]',
        'disabled:cursor-not-allowed',
    ],
    {
        variants: {
            size: {
                small: 'text-sm',
                medium: 'text-sm',
                large: 'text-base',
            },
        },

        defaultVariants: {
            size: 'medium',
        },
    },
)

export type TextFieldSize =
    NonNullable<
        VariantProps<
            typeof textFieldControlVariants
        >['size']
    >

export interface TextFieldProps
    extends Omit<
        AriaTextFieldProps,
        'children' | 'className'
    > {
    label: ReactNode

    description?: ReactNode
    errorMessage?: FormFieldErrorMessage

    leadingContent?: ReactNode
    trailingContent?: ReactNode

    size?: TextFieldSize

    spellcheck?: boolean
    placeholder?: string

    className?: string
    controlClassName?: string
    inputClassName?: string
}

export const TextField =
    forwardRef<
        HTMLInputElement,
        TextFieldProps
    >(function TextField(
        {
            label,
            description,
            errorMessage,
            leadingContent,
            trailingContent,
            spellcheck,
            size = 'medium',
            className,
            controlClassName,
            inputClassName,
            isRequired = false,
            isInvalid,
            validationBehavior = 'aria',
            placeholder,
            ...textFieldProps
        },
        ref,
    ) {
        /*
         * A static error message usually means an external
         * validator such as React Hook Form/Zod has marked
         * the field invalid.
         */
        const hasStaticError =
            typeof errorMessage === 'string' &&
            errorMessage.trim().length > 0

        const resolvedIsInvalid =
            isInvalid ?? hasStaticError

        return (
            <AriaTextField
                {...textFieldProps}
                isRequired={isRequired}
                isInvalid={resolvedIsInvalid}
                validationBehavior={
                    validationBehavior
                }
                className={cn(
                    'flex w-full flex-col gap-1.5',
                    className,
                )}
            >
                {({
                    isInvalid:
                    fieldIsInvalid,
                    isDisabled,
                    isReadOnly,
                }) => (
                    <FormField
                        label={label}
                        description={description}
                        errorMessage={errorMessage}
                        isRequired={isRequired}
                    >
                        <div
                            className={cn(
                                textFieldControlVariants({
                                    size,
                                    isInvalid:
                                        fieldIsInvalid,
                                    isDisabled,
                                    isReadOnly,
                                }),
                                controlClassName,
                            )}
                        >
                            {leadingContent ? (
                                <span
                                    aria-hidden="true"
                                    className={[
                                        'flex shrink-0',
                                        'items-center justify-center',
                                        'text-(--color-muted-foreground)',
                                    ].join(' ')}
                                >
                                    {leadingContent}
                                </span>
                            ) : null}

                            <AriaInput
                                ref={ref}
                                placeholder={placeholder}
                                aria-placeholder={placeholder}
                                spellCheck={spellcheck}
                                className={cn(
                                    textFieldInputVariants({
                                        size,
                                    }),
                                    inputClassName,
                                )}
                            />

                            {trailingContent ? (
                                <span className="flex shrink-0 items-center justify-center">
                                    {trailingContent}
                                </span>
                            ) : null}
                        </div>
                    </FormField>
                )}
            </AriaTextField>
        )
    })
